/**
 * Define the data structure storing the information of a file to upload.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com
 * Copyright © Ircam amplify 2024
 */

import { safariSupportedMimeList } from '@/components/ModuleConfig/ModuleDef'
import { FileConstraints } from '@/components/ModuleConfig/ModuleInputConfig'
import { FileUtil, Util } from '@amplify3/duplo-back-end'

export class AudioFileCheck {
  /**
   * Returns true if the requirements are met,
   * false if one of the requirement is not met,
   * or undefined if the checking is not done yet.
   */
  isValid() {
    // Check if the requirements have been verified.
    if (
      this.isTypeValid === undefined &&
      this.isMinDurationValid === undefined &&
      this.isMaxDurationValid === undefined &&
      this.isChannelCountValid === undefined
    ) {
      return undefined
    }

    // Check that all members are not false.
    // Must be compare with false explicitly with `!== false`,
    // because undefined is an accepted value (it means that the feature or
    // specification which correponds to has no requirement).
    return (
      this.isTypeValid !== false &&
      this.isMinDurationValid !== false &&
      this.isMaxDurationValid !== false &&
      this.isChannelCountValid !== false
    )
  }

  requirements: FileConstraints

  /**
   * True of the file format is supported.
   */
  isTypeValid: boolean | undefined

  /**
   * True of the minimum duration fulfill the requirements.
   */
  isMinDurationValid: boolean | undefined

  /**
   * True of the maximum duration fulfill the requirements.
   */
  isMaxDurationValid: boolean | undefined

  /**
   * True of the channel count fulfill the requirements.
   */
  isChannelCountValid: boolean | undefined

  /**
   * Constructor.
   */
  constructor(
    requirements: FileConstraints,
    isMinDurationValid: boolean | undefined = undefined,
    isMaxDurationValid: boolean | undefined = undefined,
    isChannelCountValid: boolean | undefined = undefined
  ) {
    this.requirements = requirements
    this.isMinDurationValid = isMinDurationValid
    this.isMaxDurationValid = isMaxDurationValid
    this.isChannelCountValid = isChannelCountValid
  }
}

/**
 * Audio specifications of an audio file or an audio buffer.
 */
export class AudioSpec {
  /**
   * The duration in number of seconds.
   */
  durationInSec: number | undefined

  /**
   * The number of channels.
   */
  channelCount: number | undefined

  /**
   * Constructor.
   */
  constructor(
    durationInSec: number | undefined = undefined,
    channelCount: number | undefined = undefined
  ) {
    this.durationInSec = durationInSec
    this.channelCount = channelCount
  }

  /**
   * Extract the audio specifications from the response of a request sent
   * to the Storage module of the back-end.
   */
  parseBackEndResponse(fileInfo: string) {
    const jsonInfo = JSON.parse(fileInfo)

    if (jsonInfo.duration && jsonInfo.channels) {
      this.durationInSec = Number(jsonInfo.duration)
      this.channelCount = Number(jsonInfo.channels)
    }
  }

  /**
   * Extract the audio specifications by decoding the file
   * using the browser's native Audio decoder.
   */
  async decodeAudioFile(
    audioContext: AudioContext,
    localFile: File
  ): Promise<void> {
    if (Util.isSafari()) {
      // Safari's error management for unsupported files is unstable.
      // When calling localFile.arrayBuffer() on an unsupported file,
      // Safari seems to exit from the code without throwing an exception.
      // The mime-type must checked before trying to load a file.
      if (!(localFile.type in safariSupportedMimeList)) {
        throw new Error('File not supported natively by Safari.')
      }
    }

    const arrayBuffer = await localFile.arrayBuffer()
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

    if (audioBuffer?.duration && audioBuffer?.numberOfChannels) {
      this.durationInSec = audioBuffer.duration
      this.channelCount = audioBuffer.numberOfChannels
    }
  }
}

/**
 * Data structure storing the information of a local file or a remote file.
 */
export class UploadFileInfo {
  /**
   * The mime-type of the file.
   */
  mimetype: string

  /**
   * The local file to uploaded.
   * @type {File}
   */
  localFile: File | undefined

  /**
   * True if already upload to the remote storage.
   * @type {boolean}
   */
  isUploaded: boolean

  /**
   * The ias:// URL in case of remote file.
   */
  remoteFile: Record<string, any> | undefined

  /**
   * The file name.
   */
  fileName: string

  /**
   * The file extension.
   */
  extension: string | undefined

  /**
   * The size of the file in bytes.
   */
  size: number

  /**
   * The checking of the requirements.
   */
  requirementCheck?: AudioFileCheck | undefined

  /**
   * Constructor.
   * @param mimetype - The mime-type of the file.
   * @param localFile - The local file to be uploaded.
   * @param isUploaded - True if already upload to the remote
   * @param remoteFile - JSON information about the remote file.
   * @param size - The size in bytes of the file.
   */
  constructor(
    mimetype: string,
    localFile: File | undefined = undefined,
    isUploaded: boolean = false,
    remoteFile: Record<string, any> | undefined = undefined,
    fileName: string | undefined = undefined,
    extension: string | undefined = undefined,
    size: number | undefined = undefined,
    requirementCheck: AudioFileCheck | undefined = undefined
  ) {
    this.mimetype = mimetype
    this.localFile = localFile
    this.isUploaded = isUploaded
    this.remoteFile = remoteFile
    this.fileName = fileName ?? localFile?.name ?? remoteFile?.filename ?? ''
    this.size = size ?? localFile?.size ?? remoteFile?.size ?? 0
    this.extension = extension ?? FileUtil.getFileExtension(this.fileName)
    this.requirementCheck = requirementCheck
  }

  /**
   * Create an instance of UploadFileInfo from a local file.
   * @param localFile The path and file name of the local file.
   * @param isUploaded True is already uploaded to the remote storage.
   * @returns An instance of UploadFileInfo.
   */
  static fromLocalFile(localFile: File, isUploaded: boolean = false) {
    return new UploadFileInfo(localFile.type, localFile, isUploaded)
  }

  /**
   * Create an instance of UploadFileInfo from a local file.
   * @param requestResponse The response of the Storage get request.
   *  In case of request posted with Axios, requestResponse should be set to
   *  response.data member, with response being the structyre of the response.
   * @param mimetype - The mime-type of the file.
   * @returns An instance of UploadFileInfo.
   */
  static fromStorageGetRequest(
    requestResponse: Record<string, any>,
    mimetype: string | undefined = undefined
  ) {
    const type = mimetype ?? requestResponse.filename
    return new UploadFileInfo(type, undefined, true, requestResponse)
  }
}

/**
 * Returns true if the file meets all requirements if they exist.
 *
 * @param file An instance of UploadFileInfo.
 */
export function isRequirementMet(file: UploadFileInfo): boolean {
  return !file.requirementCheck || file.requirementCheck.isValid() === true
}

/**
 * Returns true if the file is uploaded and has an ias URL.
 *
 * @param file An instance of UploadFileInfo.
 */
export function isFileUploaded(uploadedFile: UploadFileInfo): boolean {
  return uploadedFile.isUploaded && uploadedFile.remoteFile != undefined
}
